import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";
import api from "../api";
import DateRangePicker from "../components/DateRangePicker";
import CustomSelect from "../components/CustomSelect";
import ClientModal from "../components/dashboard/ClientModal";
import DriverModal from "../components/dashboard/DriverModal";
import CarModal from "../components/dashboard/CarModal";
import DownloadIcon from "../components/icons/DownloadIcon";
import { FaPlus } from "react-icons/fa";
import ReportCard from "../components/ReportCard";
import { Bar, Line, Pie } from "react-chartjs-2";
import "chart.js/auto";
import { format } from "date-fns";
import { useSearchParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  scales,
} from "chart.js";
import ModalCenter from "../components/ModalCenter";
import ModalWashes from "../components/ModalWashes";
import Spinner from "../components/Spinner";

// Registrar módulos de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function ReportsPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [user, setUser] = useState({});
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState("viajes"); // Estado para la sección activa

  const [data, setData] = useState([]);
  const [washes, setWashes] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();

  const [selectedRange, setSelectedRange] = useState(null);
  const [clients, setClients] = useState([]);
  const [client, setClient] = useState("");
  const [drivers, setDrivers] = useState([]);
  const [driver, setDriver] = useState("");
  const [vehicleType, setVehicleType] = useState("");
  const [cars, setCars] = useState([]);
  const [car, setCar] = useState("");

  const [flag, setFlag] = useState(false);

  // Filtros para la segunda sección
  const [washesRange, setWashesRange] = useState(null);
  const [washesDriver, setWashesDriver] = useState("");

  const [isAddNewModalOpen, setIsAddNewModalOpen] = useState(false);
  const [addNewType, setAddNewType] = useState(""); // Tipo de entidad: 'client', 'driver', 'car'

  const [quoterRides, setQuoterRides] = useState(false);
  const [openModalWash, setOpenModalWash] = useState(false);

  const [newClientAdded, setNewClientAdded] = useState(false);
  const [newDriverAdded, setNewDriverAdded] = useState(false);
  const [newCarAdded, setNewCarAdded] = useState(false);

  const vehicleTypeOptions = [
    { value: 1, label: "Alta Gamma" },
    { value: 2, label: "Estándar" },
  ];

  const handleAddNew = (type) => {
    setOpenModalWash(false); // Cerrar el modal actual
    setAddNewType(type); // Guardar el tipo de entidad
    setIsAddNewModalOpen(true); // Abrir el modal para agregar
  };

  const closeModal = () => {
    setAddNewType("");
    setIsAddNewModalOpen(false);
    setOpenModalWash(true);
  };

  const handleClientAdded = (isAdded) => {
    setNewClientAdded(isAdded); // Actualiza el estado para indicar que se agregó un nuevo cliente
  };

  const handleDriverAdded = (isAdded) => {
    setNewDriverAdded(isAdded); // Actualiza el estado para indicar que se agregó un nuevo conductor
  };

  const handleCarAdded = (isAdded) => {
    setNewCarAdded(isAdded); // Actualiza el estado para indicar que se agregó un nuevo vehiculo
  };

  const handleRangeSelect = (range) => {
    console.log("Rango seleccionado:", range);
    setSelectedRange(range);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        if (activeSection === "viajes") {
          // Obtener filtros desde los parámetros de la URL
          const dateFrom = searchParams.get("date_from");
          const dateTo = searchParams.get("date_to");
          const clientParam = searchParams.get("client");
          const driverParam = searchParams.get("driver");
          const category = searchParams.get("category");
          const vehicle = searchParams.get("vehicle");

          const filters = {
            date_from: selectedRange?.startDate
              ? format(new Date(selectedRange.startDate), "yyyy-MM-dd")
              : dateFrom,
            date_to: selectedRange?.endDate
              ? format(new Date(selectedRange.endDate), "yyyy-MM-dd")
              : dateTo,
            client: client || clientParam,
            driver: driver || driverParam,
            category: vehicleType || category,
            vehicle: car || vehicle,
          };

          const validFilters = Object.fromEntries(
            Object.entries(filters).filter(
              ([_, value]) =>
                value !== undefined && value !== null && value !== "ALL"
            )
          );

          // Ejecutar todas las solicitudes en paralelo
          const [
            meResponse,
            clientsResponse,
            driversResponse,
            carsResponse,
            reportData,
          ] = await Promise.all([
            api.users.getMe(),
            api.clients.getMulti(),
            api.drivers.getMulti(),
            api.vehicles.getMulti(),
            api.rides.getReport(validFilters), // Pasar filtros
          ]);

          // Actualizar estados correspondientes
          setUser(meResponse);
          setClients(clientsResponse);
          setDrivers(driversResponse);
          setCars(carsResponse);
          setData(reportData);
          setFlag(false);
        } else if (activeSection === "lavados") {
          const dateFrom = searchParams.get("date_from");
          const dateTo = searchParams.get("date_to");
          const driverParam = searchParams.get("driver");
          // Filtros específicos para "lavados"
          const filters = {
            date_from: washesRange?.startDate
              ? format(new Date(washesRange.startDate), "yyyy-MM-dd")
              : dateFrom,
            date_to: washesRange?.endDate
              ? format(new Date(washesRange.endDate), "yyyy-MM-dd")
              : dateTo,
            driver: washesDriver || driverParam,
          };

          const validFilters = Object.fromEntries(
            Object.entries(filters).filter(
              ([_, value]) =>
                value !== undefined && value !== null && value !== "ALL"
            )
          );

          // Obtener datos de lavados
          const washesData = await api.washes.getReport(validFilters);
          setWashes(washesData);
          setFlag(false);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    searchParams,
    activeSection, // Cambiar entre "viajes" y "lavados"
    newClientAdded, // Actualizaciones en clientes
    newDriverAdded, // Actualizaciones en conductores
    newCarAdded, // Actualizaciones en vehículos
    flag === true,
  ]);

  // Función para limpiar filtros
  const clearFilters = () => {
    setSelectedRange(null);
    setClient("");
    setDriver("");
    setVehicleType("");
    setCar("");
    setWashesRange(null);
    setWashesDriver("");
    setSearchParams({});
  };

  // Función para aplicar filtros
  const applyFilters = async () => {
    try {
      setLoading(true);

      // Preparar los filtros
      const filters = {
        date_from: selectedRange?.startDate
          ? format(new Date(selectedRange.startDate), "yyyy-MM-dd")
          : undefined,
        date_to: selectedRange?.endDate
          ? format(new Date(selectedRange.endDate), "yyyy-MM-dd")
          : undefined,
        client: client || undefined,
        driver: driver || undefined,
        category: vehicleType || undefined,
        vehicle: car || undefined,
        section: activeSection,
      };

      // Filtrar solo los parámetros definidos
      const validFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) =>
            value !== undefined && value !== null && value !== "ALL"
        )
      );

      // Actualizar los parámetros de la URL
      setSearchParams(validFilters);

      // Obtener los datos del reporte basado en los filtros
      const reportData = await api.rides.getReport(validFilters);

      // Actualizar el estado con el reporte
      setData(reportData);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Función para aplicar filtros
  const applyFiltersWashes = async () => {
    try {
      setLoading(true);

      // Preparar los filtros
      const filters = {
        date_from: washesRange?.startDate
          ? format(new Date(washesRange.startDate), "yyyy-MM-dd")
          : undefined,
        date_to: washesRange?.endDate
          ? format(new Date(washesRange.endDate), "yyyy-MM-dd")
          : undefined,
        driver: washesDriver || undefined,
        section: activeSection,
      };

      // Filtrar solo los parámetros definidos
      const validFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) =>
            value !== undefined && value !== null && value !== "ALL"
        )
      );

      // Actualizar los parámetros de la URL
      setSearchParams(validFilters);

      // Obtener los datos del reporte basado en los filtros
      const reportData = await api.washes.getReport(validFilters);

      // Actualizar el estado con el reporte
      setWashes(reportData);
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Configuración de datos para "Variación de traslados"
  const demandData = {
    labels: data?.demandVariation
      ?.map((e) => e.date)
      .filter((value, index, self) => self.indexOf(value) === index),
    datasets: [
      {
        label: "traslados",
        data: data?.demandVariation?.map((e) => e.totalRides),
        borderColor: "#F3BC00",
        backgroundColor: "transparent",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Configuración de datos para "Variación de facturación"
  const revenueData = {
    labels: data?.billingVariation
      ?.map((e) => e.date)
      .filter((value, index, self) => self.indexOf(value) === index),
    datasets: [
      {
        label: "Monto",
        data: data?.billingVariation?.map((e) => e.amount),
        borderColor: "#02AF82",
        backgroundColor: "transparent",
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Gráfico de traslados por conductor
  const ridesPerDriverData = {
    labels: data?.ridesPerDriver?.map(
      (driver) => driver?.driver_Name || "Sin asignar"
    ),
    datasets: [
      {
        label: "Traslados",
        data: data?.ridesPerDriver?.map((driver) => driver?.totalRides),
        backgroundColor: "#5C6B9C",
      },
    ],
  };

  // Gráfico de traslados por vehículo
  const ridesPerVehicleData = {
    labels: data?.ridesPerVehicle?.map(
      (vehicle) => `${vehicle.vehicle_LicensePlate || "Sin placa"}`
    ),
    datasets: [
      {
        label: "Traslados",
        data: data?.ridesPerVehicle?.map((vehicle) => vehicle.totalRides),
        backgroundColor: "#C1AC3D",
        borderWidth: 1,
      },
    ],
  };

  // Gráfico de tipo de traslado
  const ridesPerServiceTypeData = {
    labels: data?.ridesPerServiceType?.map(
      (service) => service?.serviceTypeName
    ),
    datasets: [
      {
        data: data?.ridesPerServiceType?.map((service) => service?.totalRides),
      },
    ],
  };

  // Gráfico de satisfacción del cliente
  const clientSatisfactionData = {
    labels: ["Satisfechos", "Insatisfechos"],
    datasets: [
      {
        data: [
          data?.clientSatisfaction?.totalSatisfied,
          data?.clientSatisfaction?.totalUnsatisfied,
        ],
        backgroundColor: ["#0876A2", "#FF8469"],
      },
    ],
  };

  // Calcular los lavados acumulados por mes para cada vehículo
  const accumulatedWashes = washes?.washesPerVehicle?.map((vehicle) => {
    const accumulatedData = vehicle.monthlyWashes.reduce((acc, wash, index) => {
      // Si es el primer mes, es igual al valor mensual
      const accumulatedValue =
        index === 0 ? wash.washCount : acc[index - 1] + wash.washCount;
      acc.push(accumulatedValue);
      return acc;
    }, []);

    return {
      ...vehicle,
      accumulatedWashes: accumulatedData, // Añadir datos acumulados a cada vehículo
    };
  });

  // Crear los datasets para cada vehículo con los lavados acumulados
  const vehicleAccumulatedDatasets =
    accumulatedWashes?.map((vehicle) => ({
      label: `${vehicle.vehicleName} (${vehicle.licensePlate}) - Acumulado`,
      data: vehicle.accumulatedWashes, // Usar los lavados acumulados por vehículo
      backgroundColor: `rgba(${Math.floor(Math.random() * 255)}, ${Math.floor(
        Math.random() * 255
      )}, ${Math.floor(Math.random() * 255)}, 0.6)`,
      stack: `accumulated-${vehicle.licensePlate}`,
    })) || [];

  const generateColor = (key) => {
    // Convierte el identificador único en un valor numérico
    let hash = 0;
    for (let i = 0; i < key.length; i++) {
      hash = key.charCodeAt(i) + ((hash << 5) - hash);
    }
    // Genera valores RGB basados en el hash
    const r = (hash >> 16) & 0xff;
    const g = (hash >> 8) & 0xff;
    const b = hash & 0xff;
    return `rgba(${r}, ${g}, ${b}, 0.6)`;
  };

  // Crear datasets con colores consistentes
  const vehicleDatasets =
    washes?.washesPerVehicle?.map((vehicle) => ({
      label: `${vehicle.vehicleName} (${vehicle.licensePlate}) - Mensual`,
      data: vehicle.monthlyWashes.map((wash) => wash.washCount),
      backgroundColor: generateColor(vehicle.licensePlate), // Color determinístico basado en la placa
      stack: `monthly-${vehicle.licensePlate}`,
    })) || [];

  // Combinar todos los datasets (mensuales + acumulados)
  const datasets = [
    ...vehicleDatasets, // Agregar los lavados mensuales
    //...vehicleAccumulatedDatasets, // Agregar los acumulados por vehículo
  ];

  // Definir las etiquetas (meses) para el gráfico
  const months =
    washes?.washesPerVehicle?.[0]?.monthlyWashes?.map((wash) => wash.month) ||
    [];

  // Configuración del gráfico
  const washesPerVehicleChart = {
    labels: months.length > 0 ? months : ["Sin datos"],
    datasets: datasets, // Incluir todos los datasets
  };

  // Opciones del gráfico
  const options = {
    responsive: true,
    indexAxis: "y", // Gráfico horizontal
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            return `${context.dataset.label}: ${context.raw} lavados`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        title: {
          display: true,
          text: "Número de Lavados",
        },
        beginAtZero: true,
        ticks: {
          stepSize: 1,
          callback: (value) => Number(value), // Asegura que no se muestren decimales
        },
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: "Meses",
        },
      },
    },
  };

  const washesPerDriver = {
    labels: washes?.washesPerDriver?.map((wash) => wash?.driver_Name),
    datasets: [
      {
        data: washes?.washesPerDriver?.map((wash) => wash?.washCount),
      },
    ],
  };

  const washesPerTypeNames = [];
  const washesPerTypeValues = [];

  // Mapeo de nombres de campos
  const nombreMapeo = {
    vacuum: "Aspirado",
    shower: "Ducha",
    complete: "Completo",
  };

  // Verificar si 'washes' y 'washes.washesPerType' están definidos
  if (washes && washes.washesPerType) {
    // Guardar los nombres de los campos en la variable usando el mapeo
    washesPerTypeNames.push(
      ...Object.keys(washes.washesPerType).map((key) => nombreMapeo[key] || key)
    );

    // Guardar los valores en la otra variable
    washesPerTypeValues.push(...Object.values(washes.washesPerType));
  }
  // Crear el objeto para usar en tu gráfico
  const washesPerType = {
    labels: washesPerTypeNames,
    datasets: [
      {
        data: washesPerTypeValues,
      },
    ],
  };

  // Opciones comunes para los gráficos
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: "top",
      },
      title: {
        display: false,
      },
    },
    scales: {
      y: {
        ticks: {
          stepSize: 1,
          callback: (value) => Number(value), // Asegura que no se muestren decimales
        },
      },
    },
  };

  const chartOptionsStacked = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
      },
      tooltip: {
        mode: "index",
        intersect: false,
      },
    },
    scales: {
      x: {
        stacked: true, // Apilamiento en el eje X
        title: {
          display: false,
          text: "Vehículo",
        },
      },
      y: {
        stacked: true, // Apilamiento en el eje Y
        title: {
          display: false,
          text: "Número de traslados",
        },
        ticks: {
          stepSize: 1, // Fuerza incrementos de 1
          callback: (value) => Number(value), // Asegura que no se muestren decimales
        },
      },
    },
  };

  const chartOptionsPie = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left",
        labels: {
          boxWidth: 10, // Reducir el tamaño del cuadro de color
          font: {
            size: 10, // Reducir el tamaño de la fuente
          },
        },
      },
      title: {
        display: false,
      },
    },
  };

  const chartOptionsPie2 = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "left",
      },
      title: {
        display: false,
      },
    },
  };

  const handleDownloadRidesReport = async (currency) => {
    try {
      setLoading(true);

      // Construir los filtros
      const filters = {
        date_from: selectedRange?.startDate
          ? format(new Date(selectedRange.startDate), "yyyy-MM-dd")
          : undefined,
        date_to: selectedRange?.endDate
          ? format(new Date(selectedRange.endDate), "yyyy-MM-dd")
          : undefined,
        client: client || undefined,
        driver: driver || undefined,
        category: vehicleType || undefined,
        vehicle: car || undefined,
      };

      // Filtrar solo los parámetros válidos
      const validFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) => value !== undefined && value !== "ALL"
        )
      );

      // Llamar a la función de API con el tipo de respuesta como "blob"
      const response = await api.rides.downloadReport(validFilters, currency);

      // Crear el archivo y descargarlo
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Extraer el nombre del archivo desde el encabezado Content-Disposition
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "") ||
          "reporte.xlsx"
        : "reporte.xlsx";

      // Crear un enlace para descargar el archivo
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Liberar memoria
      URL.revokeObjectURL(link.href);
      setQuoterRides(false);
    } catch (error) {
      console.error("Error descargando el reporte:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadWashesReport = async () => {
    try {
      setLoading(true);

      // Construir los filtros
      const filters = {
        date_from: washesRange?.startDate
          ? format(new Date(washesRange.startDate), "yyyy-MM-dd")
          : undefined,
        date_to: washesRange?.endDate
          ? format(new Date(washesRange.endDate), "yyyy-MM-dd")
          : undefined,
        driver: washesDriver || undefined,
      };

      // Filtrar solo los parámetros válidos
      const validFilters = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) => value !== undefined && value !== "ALL"
        )
      );

      // Llamar a la función de API con el tipo de respuesta como "blob"
      const response = await api.washes.getDownloadReport(validFilters);

      // Crear el archivo y descargarlo
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Extraer el nombre del archivo desde el encabezado Content-Disposition
      const contentDisposition = response.headers["content-disposition"];
      const fileName = contentDisposition
        ? contentDisposition.split("filename=")[1]?.replace(/"/g, "") ||
          "reporte.xlsx"
        : "reporte.xlsx";

      // Crear un enlace para descargar el archivo
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();

      // Liberar memoria
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error descargando el reporte:", error);
    } finally {
      setLoading(false);
    }
  };

  const [opacityLevel, setOpacityLevel] = useState(0);

  useEffect(() => {
    if (loading) {
      let intervalId = setInterval(() => {
        setOpacityLevel((prev) => Math.min(prev + 0.1, 1)); // Incrementa la opacidad hasta 1
      }, 500); // Ajusta el intervalo para controlar la velocidad
      return () => clearInterval(intervalId); // Limpia el intervalo al desmontar
    } else {
      setOpacityLevel(1); // Asegúrate de que la opacidad esté al máximo al terminar de cargar
    }
  }, [loading]);

  useEffect(() => {
    // Al cargar la página, revisamos si hay un parámetro "section" en la URL
    const sectionParam = searchParams.get("section");
    if (sectionParam) {
      setActiveSection(sectionParam); // Actualizar el estado con el valor de la URL
    }
  }, [searchParams]);

  const handleSectionChange = (section) => {
    clearFilters();
    setActiveSection(section); // Cambiar el estado local
    setSearchParams((prevParams) => {
      const newParams = new URLSearchParams();
      newParams.set("section", section); // Agregar "section" a los parámetros de la URL
      return newParams;
    });
  };

  return (
    <Layout>
      <div
        className={`p-8 space-y-10 bg-[#F8F9FB] transition-opacity ease-in-out`}
        style={{
          opacity: opacityLevel,
          transition: "opacity 0.5s linear",
        }}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <h1 className="poppins-bold text-lg text-[#1F384C] tracking-wider">
              Reportes
            </h1>

            {error && <p className="text-red-600 mb-4">{error}</p>}

            {/* Botones de navegación */}
            <div className="flex space-x-4 mb-8">
              <button
                onClick={() => {
                  handleSectionChange("viajes");
                }}
                className={`inter-medium py-2 px-3 rounded-lg text-sm ${
                  activeSection === "viajes"
                    ? "bg-[#FFD2D2] text-[#D35B66]"
                    : "bg-[#FFFFFF] text-[#4B5563]"
                }`}
              >
                Reportes de viajes
              </button>
              <button
                onClick={() => {
                  handleSectionChange("lavados");
                }}
                className={`inter-medium py-2 px-3 rounded-lg text-sm ${
                  activeSection === "lavados"
                    ? "bg-[#FFD2D2] text-[#D35B66]"
                    : "bg-[#FFFFFF] text-[#4B5563]"
                }`}
              >
                Reportes de lavados
              </button>
            </div>

            {/* Contenido dinámico según la sección activa */}
            <div>
              {/* Renderizado condicional del modal */}
              {isAddNewModalOpen && (
                <>
                  {addNewType === "client" && (
                    <ClientModal
                      isOpen={isAddNewModalOpen}
                      onClose={closeModal}
                      onClientAdded={handleClientAdded}
                    />
                  )}
                  {addNewType === "driver" && (
                    <DriverModal
                      isOpen={isAddNewModalOpen}
                      onClose={closeModal}
                      onDriverAdded={handleDriverAdded}
                    />
                  )}
                  {addNewType === "car" && (
                    <CarModal
                      isOpen={isAddNewModalOpen}
                      onClose={closeModal}
                      onCarAdded={handleCarAdded}
                    />
                  )}
                </>
              )}
              <ModalCenter
                title="Descargar reporte de traslados"
                subtitle="Selecciona una moneda"
                isOpen={quoterRides}
                onClose={() => setQuoterRides(false)}
                onClick={handleDownloadRidesReport}
                onlyCurrency
              />

              <ModalWashes
                title="Registrar lavado de vehiculo"
                isOpen={openModalWash}
                onClose={() => setOpenModalWash(false)}
                onAddDriver={() => {
                  handleAddNew("driver");
                }}
                onAddCar={() => {
                  handleAddNew("car");
                }}
                onClick={() => setFlag(true)}
                btnText="Guardar"
              />

              {activeSection === "viajes" ? (
                <div>
                  <div className="flex flex-col gap-4">
                    <p className="poppins-regular text-[#8F99A6] tracking-wider text-xs">
                      Filtrar por:
                    </p>
                    <div className="poppins-regular flex items-center gap-4 flex-wrap">
                      <DateRangePicker
                        selectRange={selectedRange}
                        startDate={selectedRange?.startDate}
                        endDate={selectedRange?.endDate}
                        onRangeChange={handleRangeSelect}
                        placeholder="Rango de fecha"
                        loading={loading}
                      />
                      <CustomSelect
                        options={clients}
                        value={client}
                        onChange={setClient}
                        placeholder="Cliente"
                        valueKey="id"
                        labelKey="name"
                        isAutocomplete
                        onAddNew={() => {
                          handleAddNew("client");
                        }}
                      />

                      <CustomSelect
                        options={drivers}
                        value={driver}
                        onChange={setDriver}
                        placeholder="Conductor"
                        valueKey="id"
                        labelKey="name"
                        isAutocomplete
                        onAddNew={() => {
                          handleAddNew("driver");
                        }}
                      />

                      <CustomSelect
                        options={vehicleTypeOptions}
                        value={vehicleType}
                        onChange={setVehicleType}
                        placeholder="Categoria"
                      />

                      <CustomSelect
                        options={cars}
                        value={car}
                        onChange={setCar}
                        placeholder="Vehículo"
                        valueKey="id"
                        labelKey="name"
                        isAutocomplete
                        onAddNew={() => {
                          handleAddNew("car");
                        }}
                      />

                      <button
                        className="poppins-medium flex items-center justify-center bg-[rgba(0,128,255,0.17)] text-[#0080FF] text-xs py-4 px-4 rounded-2xl tracking-wider"
                        onClick={applyFilters}
                      >
                        Aplicar filtros
                      </button>
                      <button
                        className="poppins-medium flex items-center justify-center bg-transparent border text-[#0080FF] text-xs py-4 px-4 rounded-2xl tracking-wider"
                        onClick={clearFilters}
                      >
                        Limpiar filtros
                      </button>
                      <button
                        className="poppins-medium flex items-center justify-center bg-[#D35B66] text-white text-xs py-4 px-4 rounded-2xl tracking-wider ml-auto"
                        onClick={() => setQuoterRides(true)}
                      >
                        <div style={{ marginRight: 15 }}>
                          <DownloadIcon size={18} color="white" />
                        </div>
                        Descargar reporte
                      </button>
                    </div>
                  </div>

                  <div className="poppins-regular flex items-center gap-4 mt-[25px] flex-wrap">
                    <ReportCard
                      title="Total de traslados"
                      titleColor="#D35B66"
                      value={data.totalRides}
                    />
                    <ReportCard
                      title="Puntaje promedio por evaluación del servicio"
                      titleColor="#4AA785"
                      value={data.averageRating?.toFixed(2)}
                    />
                    <ReportCard
                      title="Total de clientes que solicitaron el servicio"
                      titleColor="#FFA500"
                      value={data.totalClients}
                    />
                  </div>
                  <div className="poppins-regular flex items-center justify-between gap-4 mt-[25px] flex-wrap">
                    {/* Gráfico de "Variación de demanda" */}
                    <div className="bg-white p-4 shadow-md md:w-[49%]">
                      <h2 className="inter-semibold text-sm text-[#6B7280]">
                        Variación de traslados
                      </h2>

                      <Line data={demandData} options={chartOptions} />
                    </div>

                    {/* Gráfico de "Variación de facturación" */}
                    <div className="bg-white p-4 shadow-md md:w-[49%]">
                      <h2 className="inter-semibold text-sm text-[#6B7280]">
                        Variación de facturación
                      </h2>
                      <Line data={revenueData} options={chartOptions} />
                    </div>
                    {/* Segunda fila */}
                    {/* Gráfico de "Traslados por el conductor" */}

                    {/* Gráfico de traslados por conductor */}
                    <div className="bg-white p-4 shadow-md md:w-[30%] h-[420px]">
                      <h2 className="inter-semibold text-sm text-[#6B7280]">
                        Traslados por el conductor
                      </h2>
                      <Bar
                        data={ridesPerDriverData}
                        options={chartOptionsStacked}
                      />
                    </div>

                    {/* Gráfico de traslados por vehículo */}
                    <div className="bg-white p-4 shadow-md md:w-[30%] h-[420px]">
                      <h2 className="inter-semibold text-sm text-[#6B7280]">
                        Traslados por el vehículo
                      </h2>
                      <Bar
                        data={ridesPerVehicleData}
                        options={chartOptionsStacked}
                      />
                    </div>

                    <div className="flex flex-col gap-y-4 ">
                      {/* Gráfico de tipo de traslado */}
                      <div className="bg-white p-4 shadow-md h-full">
                        <h2 className="inter-semibold text-sm text-[#6B7280]">
                          Tipo de traslado
                        </h2>
                        <div>
                          <Pie
                            data={ridesPerServiceTypeData}
                            options={chartOptionsPie}
                          />
                        </div>
                      </div>

                      {/* Gráfico de satisfacción del cliente */}

                      <div className="bg-white p-4 shadow-md">
                        <h2 className="inter-semibold text-sm text-[#6B7280]">
                          Satisfacción del cliente
                        </h2>
                        <div>
                          <Pie
                            data={clientSatisfactionData}
                            options={chartOptionsPie2}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex flex-col gap-4">
                    <p className="poppins-regular text-[#8F99A6] tracking-wider text-xs">
                      Filtrar por:
                    </p>
                    <div className="poppins-regular w-[90%] flex items-center gap-4 flex-wrap">
                      <DateRangePicker
                        selectRange={washesRange}
                        startDate={washesRange?.startDate}
                        endDate={washesRange?.endDate}
                        onRangeChange={setWashesRange}
                        placeholder="Rango de fecha"
                        loading={loading}
                      />
                      <CustomSelect
                        options={drivers}
                        value={washesDriver}
                        onChange={setWashesDriver}
                        placeholder="Conductor"
                        valueKey="id"
                        labelKey="name"
                        isAutocomplete
                        onAddNew={() => {
                          handleAddNew("driver");
                        }}
                      />
                      <button
                        className="poppins-medium flex items-center justify-center bg-[rgba(0,128,255,0.17)] text-[#0080FF] text-xs py-4 px-4 rounded-2xl tracking-wider"
                        onClick={applyFiltersWashes}
                      >
                        Aplicar filtros
                      </button>
                      <button
                        className="poppins-medium flex items-center justify-center bg-transparent border text-[#0080FF] text-xs py-4 px-4 rounded-2xl tracking-wider"
                        onClick={clearFilters}
                      >
                        Limpiar filtros
                      </button>
                      <div className="flex gap-[20px] ml-auto">
                        <button
                          className="poppins-medium flex items-center justify-center bg-[#4AA785] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
                          onClick={() => setOpenModalWash(true)}
                        >
                          <div style={{ marginRight: 9 }}>
                            <FaPlus color="white" size={18} />
                          </div>
                          Añadir un lavado de vehículo
                        </button>
                        <button
                          className="poppins-medium flex items-center justify-center bg-[#D35B66] text-white text-xs py-4 px-4 rounded-2xl tracking-wider"
                          onClick={handleDownloadWashesReport}
                        >
                          <div style={{ marginRight: 15 }}>
                            <DownloadIcon size={18} color="white" />
                          </div>
                          Descargar reporte
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="poppins-regular flex items-center justify-between gap-4 mt-[25px] flex-wrap w-[90%]">
                    {/* Gráfico Cantidad total de lavados */}
                    <div className="bg-white p-4 shadow-md md:w-[70%] h-[420px]">
                      <h2 className="inter-semibold text-sm text-[#6B7280]">
                        Cantidad total de lavados
                      </h2>
                      <Bar data={washesPerVehicleChart} options={options} />
                    </div>

                    <div className="flex flex-col gap-y-4">
                      {/* Gráfico Total por conductor */}
                      <div className="bg-white p-4 shadow-md">
                        <h2 className="inter-semibold text-sm text-[#6B7280]">
                          Total por conductor
                        </h2>
                        <div>
                          <Pie
                            data={washesPerDriver}
                            options={chartOptionsPie}
                          />
                        </div>
                      </div>

                      {/* Gráfico Total por tipo de lavado */}

                      <div className="bg-white p-4 shadow-md">
                        <h2 className="inter-semibold text-sm text-[#6B7280]">
                          Total por tipo de lavado
                        </h2>
                        <div>
                          <Pie
                            data={washesPerType}
                            options={chartOptionsPie2}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
